"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { useMyStreamWaveMessages } from "@/contexts/wave/MyStreamContext";
import type { Drop } from "@/helpers/waves/drop.helpers";
import type { WaveMessages } from "@/contexts/wave/hooks/types";
import { useDropMessages } from "./useDropMessages";
import type { ApiWave } from "@/generated/models/ApiWave";
interface VirtualizedWaveMessages extends Omit<WaveMessages, "drops"> {
  readonly drops: Drop[];
  readonly allDropsCount: number;
  readonly loadMoreLocally: () => void;
  readonly hasMoreLocal: boolean;
  readonly fetchNextPageForDrop: DropMessagesResult["fetchNextPage"];
  readonly waitAndRevealDrop: (serialNo: number, maxWaitTimeMs?: number, pollIntervalMs?: number) => Promise<boolean>;
}
interface VirtualLimitState {
  readonly scopeKey: string;
  readonly limit: number;
}
interface AppendTrackingState {
  readonly scopeKey: string;
  readonly hasInitialized: boolean;
  readonly previousTotal: number;
  readonly effectiveLimit: number;
}
interface WaveMessagesSnapshot {
  readonly waveId: string;
  readonly messages: WaveMessages | null;
}
type DropMessagesResult = ReturnType<typeof useDropMessages>;
type ActiveMessages = WaveMessages | DropMessagesResult;
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
const getScopeKey = ({
  waveId,
  dropId,
  pageSize
}: {
  readonly waveId: string;
  readonly dropId: string | null;
  readonly pageSize: number;
}) => `${waveId}:${dropId ?? ""}:${pageSize}`;
const createAppendTrackingState = (scopeKey: string, effectiveLimit: number): AppendTrackingState => ({
  scopeKey,
  hasInitialized: false,
  previousTotal: 0,
  effectiveLimit
});
const isSameAppendTrackingState = (currentState: AppendTrackingState, nextState: AppendTrackingState): boolean => currentState.scopeKey === nextState.scopeKey && currentState.hasInitialized === nextState.hasInitialized && currentState.previousTotal === nextState.previousTotal && currentState.effectiveLimit === nextState.effectiveLimit;
const getScopedVirtualLimit = ({
  scopeKey,
  virtualLimitState,
  pageSize
}: {
  readonly scopeKey: string;
  readonly virtualLimitState: VirtualLimitState;
  readonly pageSize: number;
}): number => virtualLimitState.scopeKey === scopeKey ? virtualLimitState.limit : pageSize;
const resolveFullWaveMessagesSnapshot = ({
  waveId,
  fullWaveMessages,
  fullWaveMessagesSnapshot
}: {
  readonly waveId: string;
  readonly fullWaveMessages: WaveMessages | undefined;
  readonly fullWaveMessagesSnapshot: WaveMessagesSnapshot;
}): WaveMessagesSnapshot => {
  if (fullWaveMessages) {
    return {
      waveId,
      messages: fullWaveMessages
    };
  }
  if (fullWaveMessagesSnapshot.waveId === waveId) {
    return fullWaveMessagesSnapshot;
  }
  return {
    waveId,
    messages: null
  };
};
const isSameWaveMessagesSnapshot = (currentSnapshot: WaveMessagesSnapshot, nextSnapshot: WaveMessagesSnapshot): boolean => currentSnapshot.waveId === nextSnapshot.waveId && currentSnapshot.messages === nextSnapshot.messages;
const getNextVirtualLimitAfterAppend = ({
  currentLimit,
  hasInitialized,
  pageSize,
  previousTotal,
  totalDrops
}: {
  readonly currentLimit: number;
  readonly hasInitialized: boolean;
  readonly pageSize: number;
  readonly previousTotal: number;
  readonly totalDrops: number;
}): number => {
  if (!hasInitialized || previousTotal <= 0 || totalDrops <= previousTotal) {
    return currentLimit;
  }
  if (currentLimit >= totalDrops) {
    return currentLimit;
  }
  return Math.min(totalDrops, currentLimit + pageSize);
};
export function useVirtualizedWaveMessages(waveId, dropId, t0, wave) {
  const $ = _c(62);
  const pageSize = t0 === undefined ? 50 : t0;
  const fullWaveMessages = useMyStreamWaveMessages(waveId);
  const fullWaveMessagesForDrop = useDropMessages(waveId, dropId, wave);
  const fetchNextPageForDrop = fullWaveMessagesForDrop.fetchNextPage;
  let t1;
  if ($[0] !== dropId || $[1] !== pageSize || $[2] !== waveId) {
    t1 = getScopeKey({
      waveId,
      dropId,
      pageSize
    });
    $[0] = dropId;
    $[1] = pageSize;
    $[2] = waveId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const scopeKey = t1;
  let t2;
  if ($[4] !== fullWaveMessages || $[5] !== waveId) {
    t2 = () => ({
      waveId,
      messages: fullWaveMessages ?? null
    });
    $[4] = fullWaveMessages;
    $[5] = waveId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const [fullWaveMessagesSnapshot, setFullWaveMessagesSnapshot] = useState(t2);
  let t3;
  if ($[7] !== pageSize || $[8] !== scopeKey) {
    t3 = () => createAppendTrackingState(scopeKey, pageSize);
    $[7] = pageSize;
    $[8] = scopeKey;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const [appendTrackingState, setAppendTrackingState] = useState(t3);
  const scopedAppendTrackingState = appendTrackingState.scopeKey === scopeKey ? appendTrackingState : createAppendTrackingState(scopeKey, pageSize);
  const latestScopeKeyRef = useRef(scopeKey);
  const latestActiveMessagesRef = useRef(null);
  const latestEffectiveLimitRef = useRef(pageSize);
  let t4;
  if ($[10] !== pageSize || $[11] !== scopeKey) {
    t4 = () => ({
      scopeKey,
      limit: pageSize
    });
    $[10] = pageSize;
    $[11] = scopeKey;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const [virtualLimitState, setVirtualLimitState] = useState(t4);
  const resolvedFullWaveMessagesSnapshot = resolveFullWaveMessagesSnapshot({
    waveId,
    fullWaveMessages,
    fullWaveMessagesSnapshot
  });
  if (!isSameWaveMessagesSnapshot(fullWaveMessagesSnapshot, resolvedFullWaveMessagesSnapshot)) {
    setFullWaveMessagesSnapshot(resolvedFullWaveMessagesSnapshot);
  }
  const activeFullWaveMessages = fullWaveMessages ?? (resolvedFullWaveMessagesSnapshot.waveId === waveId ? resolvedFullWaveMessagesSnapshot.messages : null);
  const activeDropMessages = dropId ? fullWaveMessagesForDrop : null;
  const activeMessages = activeDropMessages ?? activeFullWaveMessages;
  const hasActiveMessages = Boolean(activeMessages);
  const activeMessagesCount = activeMessages?.drops.length ?? 0;
  let t5;
  if ($[13] !== pageSize || $[14] !== scopeKey || $[15] !== virtualLimitState) {
    t5 = getScopedVirtualLimit({
      scopeKey,
      virtualLimitState,
      pageSize
    });
    $[13] = pageSize;
    $[14] = scopeKey;
    $[15] = virtualLimitState;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const virtualLimit = t5;
  const persistedEffectiveLimit = scopedAppendTrackingState.effectiveLimit;
  const baseVirtualLimit = Math.max(virtualLimit, persistedEffectiveLimit);
  const effectiveVirtualLimit = hasActiveMessages ? getNextVirtualLimitAfterAppend({
    currentLimit: baseVirtualLimit,
    hasInitialized: scopedAppendTrackingState.hasInitialized,
    pageSize,
    previousTotal: scopedAppendTrackingState.previousTotal,
    totalDrops: activeMessagesCount
  }) : baseVirtualLimit;
  const hasMoreLocal = hasActiveMessages ? activeMessagesCount > effectiveVirtualLimit : false;
  const nextAppendTrackingState = hasActiveMessages ? {
    scopeKey,
    previousTotal: activeMessagesCount,
    hasInitialized: scopedAppendTrackingState.hasInitialized || activeMessagesCount > 0,
    effectiveLimit: effectiveVirtualLimit
  } : createAppendTrackingState(scopeKey, pageSize);
  if (!isSameAppendTrackingState(appendTrackingState, nextAppendTrackingState)) {
    setAppendTrackingState(nextAppendTrackingState);
  }
  let t6;
  let t7;
  if ($[17] !== activeMessages || $[18] !== effectiveVirtualLimit || $[19] !== scopeKey) {
    t6 = () => {
      latestScopeKeyRef.current = scopeKey;
      latestActiveMessagesRef.current = activeMessages ?? null;
      latestEffectiveLimitRef.current = effectiveVirtualLimit;
    };
    t7 = [activeMessages, effectiveVirtualLimit, scopeKey];
    $[17] = activeMessages;
    $[18] = effectiveVirtualLimit;
    $[19] = scopeKey;
    $[20] = t6;
    $[21] = t7;
  } else {
    t6 = $[20];
    t7 = $[21];
  }
  useEffect(t6, t7);
  let t8;
  if ($[22] !== pageSize || $[23] !== scopeKey || $[24] !== setVirtualLimitState) {
    t8 = () => {
      if (latestScopeKeyRef.current !== scopeKey) {
        return;
      }
      setVirtualLimitState(currentState => {
        const totalDrops = latestActiveMessagesRef.current?.drops.length ?? 0;
        const currentLimit = currentState.scopeKey === scopeKey ? Math.max(currentState.limit, latestEffectiveLimitRef.current) : latestEffectiveLimitRef.current;
        return {
          scopeKey,
          limit: totalDrops > currentLimit ? currentLimit + pageSize : currentLimit
        };
      });
    };
    $[22] = pageSize;
    $[23] = scopeKey;
    $[24] = setVirtualLimitState;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const loadMoreLocally = t8;
  let t9;
  if ($[26] !== scopeKey || $[27] !== setVirtualLimitState) {
    t9 = serialNo => {
      if (latestScopeKeyRef.current !== scopeKey) {
        return;
      }
      const activeDrops = latestActiveMessagesRef.current?.drops;
      if (!activeDrops) {
        return;
      }
      const index = activeDrops.findIndex(drop => drop.serial_no === serialNo);
      if (index === -1) {
        return;
      }
      const maxIndex = activeDrops.length - 1;
      const targetIndex = Math.min(index + 30, maxIndex);
      setVirtualLimitState(currentState_0 => {
        const currentLimit_0 = currentState_0.scopeKey === scopeKey ? Math.max(currentState_0.limit, latestEffectiveLimitRef.current) : latestEffectiveLimitRef.current;
        return {
          scopeKey,
          limit: Math.max(currentLimit_0, targetIndex + 1)
        };
      });
    };
    $[26] = scopeKey;
    $[27] = setVirtualLimitState;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const revealDrop = t9;
  let t10;
  if ($[29] !== revealDrop || $[30] !== scopeKey || $[31] !== waveId) {
    t10 = async (serialNo_0, t11, t12) => {
      const maxWaitTimeMs = t11 === undefined ? 3000 : t11;
      const pollIntervalMs = t12 === undefined ? 100 : t12;
      const startTime = Date.now();
      while (Date.now() - startTime < maxWaitTimeMs) {
        if (latestScopeKeyRef.current !== scopeKey) {
          return false;
        }
        if (latestActiveMessagesRef.current?.drops.some(drop_0 => drop_0.serial_no === serialNo_0)) {
          revealDrop(serialNo_0);
          return true;
        }
        await delay(pollIntervalMs);
      }
      console.warn(`useVirtualizedWaveMessages: Timed out after ${maxWaitTimeMs}ms waiting for serialNo ${serialNo_0} in wave ${waveId}`);
      return false;
    };
    $[29] = revealDrop;
    $[30] = scopeKey;
    $[31] = waveId;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const waitAndRevealDrop = t10;
  if (!activeMessages) {
    return;
  }
  let t11;
  if ($[33] !== activeMessages.drops || $[34] !== effectiveVirtualLimit) {
    t11 = activeMessages.drops.slice(0, effectiveVirtualLimit);
    $[33] = activeMessages.drops;
    $[34] = effectiveVirtualLimit;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const virtualizedDrops = t11;
  const allDropsCount = activeMessages.drops.length;
  if (activeDropMessages) {
    let t12;
    if ($[36] !== activeDropMessages.drops) {
      t12 = activeDropMessages.drops.at(-1)?.serial_no ?? null;
      $[36] = activeDropMessages.drops;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    let t13;
    if ($[38] !== activeDropMessages.hasNextPage || $[39] !== activeDropMessages.isFetching || $[40] !== activeDropMessages.isFetchingNextPage || $[41] !== allDropsCount || $[42] !== fetchNextPageForDrop || $[43] !== hasMoreLocal || $[44] !== loadMoreLocally || $[45] !== t12 || $[46] !== virtualizedDrops || $[47] !== waitAndRevealDrop || $[48] !== waveId) {
      t13 = {
        hasNextPage: activeDropMessages.hasNextPage,
        id: waveId,
        isLoading: activeDropMessages.isFetching,
        isLoadingNextPage: activeDropMessages.isFetchingNextPage,
        latestFetchedSerialNo: t12,
        drops: virtualizedDrops,
        allDropsCount,
        loadMoreLocally,
        hasMoreLocal,
        fetchNextPageForDrop,
        waitAndRevealDrop
      };
      $[38] = activeDropMessages.hasNextPage;
      $[39] = activeDropMessages.isFetching;
      $[40] = activeDropMessages.isFetchingNextPage;
      $[41] = allDropsCount;
      $[42] = fetchNextPageForDrop;
      $[43] = hasMoreLocal;
      $[44] = loadMoreLocally;
      $[45] = t12;
      $[46] = virtualizedDrops;
      $[47] = waitAndRevealDrop;
      $[48] = waveId;
      $[49] = t13;
    } else {
      t13 = $[49];
    }
    return t13;
  }
  const visibleFullWaveMessages = activeMessages as WaveMessages;
  let t12;
  if ($[50] !== allDropsCount || $[51] !== fetchNextPageForDrop || $[52] !== hasMoreLocal || $[53] !== loadMoreLocally || $[54] !== virtualizedDrops || $[55] !== visibleFullWaveMessages.hasNextPage || $[56] !== visibleFullWaveMessages.isLoading || $[57] !== visibleFullWaveMessages.isLoadingNextPage || $[58] !== visibleFullWaveMessages.latestFetchedSerialNo || $[59] !== waitAndRevealDrop || $[60] !== waveId) {
    t12 = {
      hasNextPage: visibleFullWaveMessages.hasNextPage,
      id: waveId,
      isLoading: visibleFullWaveMessages.isLoading,
      isLoadingNextPage: visibleFullWaveMessages.isLoadingNextPage,
      latestFetchedSerialNo: visibleFullWaveMessages.latestFetchedSerialNo,
      drops: virtualizedDrops,
      allDropsCount,
      loadMoreLocally,
      hasMoreLocal,
      fetchNextPageForDrop,
      waitAndRevealDrop
    };
    $[50] = allDropsCount;
    $[51] = fetchNextPageForDrop;
    $[52] = hasMoreLocal;
    $[53] = loadMoreLocally;
    $[54] = virtualizedDrops;
    $[55] = visibleFullWaveMessages.hasNextPage;
    $[56] = visibleFullWaveMessages.isLoading;
    $[57] = visibleFullWaveMessages.isLoadingNextPage;
    $[58] = visibleFullWaveMessages.latestFetchedSerialNo;
    $[59] = waitAndRevealDrop;
    $[60] = waveId;
    $[61] = t12;
  } else {
    t12 = $[61];
  }
  return t12;
}